import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';
import type { InvitationParams } from '@/auth/invitation';
import { withoutInvitationParams } from '@/auth/invitation';
import { useAuth } from '@/hooks/useAuth';

interface AcceptInvitePageProps {
  invitation: InvitationParams;
}

/**
 * Landing screen for an organization invitation. Hands the ticket back to the
 * identity provider immediately; the visible content only matters if that
 * redirect fails.
 *
 * For Auth0, `organization` here overrides the build-time
 * `VITE_AUTH0_ORGANIZATION` on the provider, which is the whole point: an
 * invitee is joining the org named on the ticket, not the org this build signs
 * people into by default. A WorkOS invitation token names its organization
 * itself, so it travels alone.
 */
export function AcceptInvitePage({ invitation }: AcceptInvitePageProps) {
  const { t } = useTranslation();
  const { isLoading, loginWithRedirect } = useAuth();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef(-1);

  useEffect(() => {
    if (isLoading || startedRef.current === attempt) return;
    startedRef.current = attempt;
    const returnTo = `${window.location.pathname}${withoutInvitationParams(
      window.location.search,
    )}`;
    void loginWithRedirect({
      appState: { returnTo },
      authorizationParams: {
        invitation: invitation.invitation,
        ...(invitation.organization ? { organization: invitation.organization } : {}),
      },
    }).catch(() => {
      setFailed(true);
    });
  }, [attempt, isLoading, invitation, loginWithRedirect]);

  const orgLabel = invitation.organizationName ?? invitation.organization;

  return (
    <main className="flex min-h-full w-full items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-2 text-foreground">
          <RapidSOSMark
            key={DEFAULT_MARK_ANIMATION}
            className="h-6 w-6"
            animation={DEFAULT_MARK_ANIMATION}
          />
          <RapidSOSWordmark className="h-6" />
        </div>

        <h1 className="mt-8 text-2xl font-semibold tracking-tight text-foreground">
          {orgLabel
            ? t('auth.invite.title', { organization: orgLabel })
            : t('auth.invite.titleNoOrganization')}
        </h1>

        {failed ? (
          <>
            <p className="mt-2 text-sm text-muted-foreground">{t('auth.invite.failed')}</p>
            <Button
              type="button"
              className="mt-6 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => {
                setFailed(false);
                setAttempt((previous) => previous + 1);
              }}
            >
              {t('common.retry')}
            </Button>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">{t('auth.invite.redirecting')}</p>
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
